import { map, filter } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance as axios, exportRequest } from '../../utils/axios';
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  data: [],
  meta: null,
  params: {
    page: 1,
    active: true,
    deleted: false,
  },
  record: null,
};

const slice = createSlice({
  name: 'hrEmployee',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getEmployeesSuccess(state, action) {
      state.isLoading = false;
      state.data = action.payload.results;
      state.meta = action.payload.meta;
      state.error = false;
    },
    getEmployeeSuccess(state, action) {
      state.isLoading = false;
      state.record = action.payload;
      state.error = false;
    },
    createEmployeeSuccess(state, action) {
      state.isLoading = false;
      state.data = [...state.data, action.payload];
      state.error = false;
    },
    updateEmployeeSuccess(state, action) {
      state.record = action.payload;
      state.isLoading = false;
      state.data = map(state.data, (_record) => (_record.id === action.payload.id ? action.payload : _record));
      state.params = { ...state.params };
      state.error = false;
    },
    deleteEmployeeSuccess(state, action) {
      state.isLoading = false;
      state.data = filter(state.data, (r) => r.id !== action.payload.id);
      state.error = false;
    },
    setParams(state, action) {
      state.params = action.payload;
    },
    refreshParams(state) {
      state.params = { ...state.params };
    },
  },
});

export default slice.reducer;
export const { setParams, refreshParams } = slice.actions;

// ----------------------------------------------------------------------

export function getEmployees(params = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/hr/employee/', { params });
      dispatch(slice.actions.getEmployeesSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function getEmployee(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/hr/employee/${id}/`);
      dispatch(slice.actions.getEmployeeSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createEmployee(record) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/hr/employee/', record);
      dispatch(slice.actions.createEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateEmployee(id, record) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(`/hr/employee/${id}/`, record);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function partialUpdateEmployee(id, record) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(`/hr/employee/${id}/`, record);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteEmployee(id, restore = false) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`/hr/employee/${id}/`, { data: { restore } });
      dispatch(slice.actions.deleteEmployeeSuccess({ id }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function actionEmployee(id, data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/hr/employee/${id}/action/`, data);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function createVacationBalance(id, data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/hr/employee/${id}/vacation-balance/`, data);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateVacationBalance(id, balanceId, data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(`/hr/employee/${id}/vacation-balance/${balanceId}/`, data);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteVacationBalance(id, balanceId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.delete(`/hr/employee/${id}/vacation-balance/${balanceId}/`);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export async function getNextYearBalance(employeeId, policyId) {
  const response = await axios.get(`/hr/employee/${employeeId}/vacation-balance/next-year/`, {
    params: { policy: policyId },
  });
  return response.data;
}

export async function getEmployeeAttendanceSummary(employeeId, year, month) {
  const response = await axios.get(`/hr/employee/${employeeId}/attendance-summary/`, {
    params: { year, month },
  });
  return response.data;
}

export function exportEmployees(params = {}) {
  return async () => {
    exportRequest('/hr/employee/export/', params, 'hr.Employee');
  };
}

export function importEmployees(file) {
  return async () => {
    const body = new FormData();
    body.append('file', file, file.name);
    const response = await axios.post('/hr/employee/import/', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  };
}

export function actionHistoryEmployee(id, data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/hr/employee/${id}/history/`, data);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function enablePortalAccess(id, data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/hr/employee/${id}/enable-portal/`, data);
      dispatch(slice.actions.updateEmployeeSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
